import React from 'react';

const publicUrl = process.env.PUBLIC_URL || '';
const singleAlleleExampleUrl = `${publicUrl}/mhcattention_example_single_allele.tsv`;
const multiAlleleExampleUrl = `${publicUrl}/mhcattention_example_multi_allele.tsv`;
const hlaeExampleUrl = `${publicUrl}/mhcattention_example_hlae.tsv`;
const supportedAllelesUrl = `${publicUrl}/mhcattention_supported_alleles.txt`;

const Tutorial: React.FC = () => {
    return <div id="content-pane" className='tutorial'>
        <h2>Tutorial</h2>
        <h4 id="general_info">General information</h4>
        <p>
            MHC Attention is a web interface for MHC class I presentation scoring and specialized HLA-E analysis. It supports both file upload and manual submission workflows,
            and the same backend rules are applied in both cases. This page summarizes the exact input formatting, peptide and allele requirements, and interpretation of the output columns.
            <ul>
                <li><b>MhcAttention</b> scores peptides against one or more class I alleles provided in your file or selected manually.</li>
                <li><b>MhcAttention HLA-E</b> scores peptides against the fixed bundled allele <b>HLA-E01:01</b>.</li>
                <li>Worker errors on the results page often come from unsupported peptide lengths, unsupported amino acid characters, or unsupported allele names.</li>
            </ul>
        </p>
        <h4>MHC Attention user interface</h4>
        <p>
            To analyze peptides with MHC Attention, you must provide the following information:
            <ul>
                <li><b>Mode:</b> choose <i>File</i> to upload a CSV or TSV file, or <i>Manual</i> to enter one peptide per line directly in the form.</li>
                <li><b>Choose model:</b> use <i>MhcAttention</i> for class I allele-specific scoring, or <i>MhcAttention HLA-E</i> for the specialized HLA-E model.</li>
                <li><b>File or peptide list:</b> uploaded files must include a header row, and manual mode must contain at least one peptide.</li>
                <li><b>Alleles:</b> class I jobs require one or more supported allele names. HLA-E jobs do not require an allele column or allele selection.</li>
                <li><b>Multi-allele strategy:</b> class I jobs support <i>Best</i> and <i>All</i> output modes when multiple alleles are provided per row.</li>
            </ul>
        </p>
        <p>After a job finishes, you can download the full output file or review a preview on the results page.</p>
        <p>The downloaded output keeps your original columns, adds the MHC Attention result columns, and may expand rows in <i>All</i> mode.</p>
        <br></br>
        <h4>MhcAttention class I data format (example files: <a href={singleAlleleExampleUrl} download>single allele</a> or <a href={multiAlleleExampleUrl} download>multi allele</a>)</h4>
        <p>The class I file format supports both tab-separated (.tsv) and comma-separated (.csv) files with the following required columns:
            <ul>
                <li><b>peptide:</b> peptide sequence to score (for example, GILGFVFTL)</li>
                <li><b>alleles:</b> one supported allele name or a comma-separated list of allele names (for example, HLA-A02:01,HLA-A03:01)</li>
            </ul>
            The required <b>peptide</b> and <b>alleles</b> header names are case-insensitive, so headers such as <b>Peptide</b> or <b>ALLELES</b> are also accepted.
            Allele names must match the supported list exactly. For human alleles, use formatting such as <b>HLA-A02:02</b>. Do not add an asterisk or change the punctuation.
            Multiple alleles must be comma-separated within the same field, and <i>up to 6 alleles are allowed per row</i>.
        </p>
        <p>The exact currently supported allele names can be downloaded here: <a href={supportedAllelesUrl} download>supported allele list</a>.</p>
        <br></br>
        <h4>MhcAttention HLA-E data format (<a href={hlaeExampleUrl} download>example file</a>)</h4>
        <p>The HLA-E format also supports both tab-separated (.tsv) and comma-separated (.csv) files, but only the following column is required:
            <ul>
                <li><b>peptide:</b> peptide sequence to score (for example, GILGFVFTL)</li>
            </ul>
            The required <b>peptide</b> header is case-insensitive.
            If an <b>alleles</b> column is present in an HLA-E file, it will be ignored. The HLA-E model always scores against the bundled allele <b>HLA-E01:01</b>.
        </p>
        <br></br>
        <h4>Manual submission format</h4>
        <p>Manual mode accepts one peptide per line in the peptide text area.
            <ul>
                <li>For <b>MhcAttention</b>, choose one or more supported alleles from the selector.</li>
                <li>For <b>MhcAttention HLA-E</b>, the allele selector is hidden because it is not needed.</li>
                <li>The backend converts manual input into the same tabular structure used for uploaded files before queuing the job.</li>
            </ul>
        </p>
        <br></br>
        <h4>Peptide requirements</h4>
        <p>All peptides are normalized to uppercase before scoring and must satisfy the following rules:
            <ul>
                <li><b>Supported peptide lengths:</b> 8, 9, 10, 11, 12, 13, 14, or 15 amino acids</li>
                <li><b>Supported amino acid tokens:</b> ARNDCQEGHILKMFPSTWYVX</li>
                <li><b>Empty peptide rows:</b> not allowed</li>
            </ul>
            Unsupported peptide lengths or unsupported amino acid characters will cause the worker to fail the job and report the error on the results page.
        </p>
        <br></br>
        <h4>Multi-allele strategy</h4>
        <p>When a class I row contains multiple alleles, MHC Attention supports two output strategies:
            <ul>
                <li><b>Best:</b> returns one output row per input row, choosing the allele with the highest percentile for that peptide.</li>
                <li><b>All:</b> returns one output row per peptide-allele pair.</li>
            </ul>
            The strategy selector is only shown for class I jobs. HLA-E jobs always use the fixed bundled allele.
        </p>
        <br></br>
        <h4>Score and percentile interpretation</h4>
        <p>MHC Attention adds the following output columns to the scored file:
            <ul>
                <li><b>alleles:</b> for class I <i>Best</i> outputs, the comma-separated allele list scored for that row; for <i>All</i> outputs, one allele per row; for HLA-E outputs, <b>HLA-E01:01</b></li>
                <li><b>mhcattention_score:</b> raw MHC Attention score on a 0 to 1 scale, where higher values indicate stronger predicted presentation</li>
                <li><b>mhcattention_pct:</b> percentile rank for that allele and peptide length relative to random human peptides. For example, a <b>99.2 percentile</b> means the score is higher than 99.2% of random human peptides scored for that allele and peptide length.</li>
                <li><b>mhcattention_best_allele:</b> included for class I <i>Best</i> outputs</li>
            </ul>
        </p>
        <br></br>
        <h4>Common input failures</h4>
        <p>The most common reasons a job fails are:
            <ul>
                <li>missing required columns such as <b>peptide</b> or, for class I jobs, <b>alleles</b></li>
                <li>uploading a file type other than CSV or TSV</li>
                <li>using unsupported peptide lengths or unsupported amino acid characters</li>
                <li>using unsupported allele names or more than 6 alleles in one row</li>
                <li>submitting an empty file or a file with no peptide rows</li>
            </ul>
        </p>
    </div>
};

export default Tutorial;
